import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";

interface MapPickerProps {
  lat?: number;
  lng?: number;
  onLocationSelect: (lat: number, lng: number) => void;
  apiKey?: string;
}

export function MapPicker({ lat, lng, onLocationSelect, apiKey }: MapPickerProps) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const markerRef = useRef<google.maps.Marker | null>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    // Load Google Maps script
    if (window.google?.maps) {
      setIsLoaded(true);
      return;
    }

    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyCtNluNHfcLUew99csGU9o593Iv79u9dP0&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => setIsLoaded(true);
    document.head.appendChild(script);

    return () => {
      // Cleanup script if component unmounts before loading
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, [apiKey]);

  useEffect(() => {
    if (!isLoaded || !mapRef.current || !window.google?.maps || mapInstanceRef.current) return;

    const initialLat = lat || 0;
    const initialLng = lng || 0;
    const center = lat && lng 
      ? { lat: initialLat, lng: initialLng }
      : { lat: 40.7128, lng: -74.0060 }; // Default to New York

    // Initialize map
    const map = new google.maps.Map(mapRef.current, {
      center,
      zoom: lat && lng ? 15 : 2,
      mapTypeControl: true,
      streetViewControl: true,
      fullscreenControl: true,
    });

    mapInstanceRef.current = map;

    // Initialize Places Autocomplete
    if (searchInputRef.current && window.google.maps.places) {
      const autocomplete = new google.maps.places.Autocomplete(searchInputRef.current, {
        types: ['geocode'],
        fields: ['geometry', 'formatted_address'],
      });

      autocompleteRef.current = autocomplete;

      // Handle place selection
      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        
        if (place.geometry?.location) {
          const newLat = place.geometry.location.lat();
          const newLng = place.geometry.location.lng();
          
          // Update map center
          map.setCenter({ lat: newLat, lng: newLng });
          map.setZoom(15);
          
          // Update or create marker
          if (markerRef.current) {
            markerRef.current.setPosition({ lat: newLat, lng: newLng });
          } else {
            const marker = new google.maps.Marker({
              position: { lat: newLat, lng: newLng },
              map,
              draggable: true,
            });
            markerRef.current = marker;

            // Update coordinates when marker is dragged
            marker.addListener("dragend", () => {
              const position = marker.getPosition();
              if (position) {
                onLocationSelect(position.lat(), position.lng());
              }
            });
          }

          onLocationSelect(newLat, newLng);
        }
      });
    }

    // Add marker if initial coordinates exist
    if (lat && lng) {
      const marker = new google.maps.Marker({
        position: { lat: initialLat, lng: initialLng },
        map,
        draggable: true,
      });
      markerRef.current = marker;

      // Update coordinates when marker is dragged
      marker.addListener("dragend", () => {
        const position = marker.getPosition();
        if (position) {
          onLocationSelect(position.lat(), position.lng());
        }
      });
    }

    // Add click listener to map
    map.addListener("click", (e: google.maps.MapMouseEvent) => {
      if (e.latLng) {
        const clickedLat = e.latLng.lat();
        const clickedLng = e.latLng.lng();
        
        // Update or create marker
        if (markerRef.current) {
          markerRef.current.setPosition({ lat: clickedLat, lng: clickedLng });
        } else {
          const marker = new google.maps.Marker({
            position: { lat: clickedLat, lng: clickedLng },
            map,
            draggable: true,
          });
          markerRef.current = marker;

          // Update coordinates when marker is dragged
          marker.addListener("dragend", () => {
            const position = marker.getPosition();
            if (position) {
              onLocationSelect(position.lat(), position.lng());
            }
          });
        }

        onLocationSelect(clickedLat, clickedLng);
      }
    });
  }, [isLoaded, onLocationSelect]);

  // Update marker position when lat/lng change externally
  useEffect(() => {
    if (mapInstanceRef.current && markerRef.current && lat && lng) {
      const position = { lat, lng };
      markerRef.current.setPosition(position);
      mapInstanceRef.current.setCenter(position);
    } else if (mapInstanceRef.current && !lat && !lng && markerRef.current) {
      // Remove marker if coordinates are cleared
      markerRef.current.setMap(null);
      markerRef.current = null;
    }
  }, [lat, lng]);

  if (!isLoaded) {
    return (
      <div className="w-full h-96 bg-muted flex items-center justify-center rounded-md">
        <p className="text-muted-foreground">Loading map...</p>
      </div>
    );
  }

  return (
    <div className="w-full space-y-2">
      <div className="space-y-2">
        <Input
          ref={searchInputRef}
          type="text"
          placeholder="Search for a location..."
          className="w-full"
        />
      </div>
      <p className="text-sm text-muted-foreground">
        Click on the map to select a location, or drag the marker to adjust.
      </p>
      <div ref={mapRef} className="w-full h-96 rounded-md border" />
    </div>
  );
}

